import clsx from 'clsx';
import Accordion from 'components/ui/ContentBlocks/Accordion';
import RichText from 'components/ui/ContentBlocks/RichText';
import Icon from 'components/ui/Icon';
import tailwind from 'utils/breakpoints';
import { useTranslation } from 'hooks/useTranslation';
import type { ICvFields } from 'types/contentful/generated';

type GroupedCv = {
  education: ICvFields[];
  professional: ICvFields[];
  award: ICvFields[];
};

function groupCv(cv: ICvFields[]): GroupedCv {
  const groupedCv: GroupedCv = {
    education: [],
    professional: [],
    award: [],
  };

  const filters = ['education', 'professional', 'award'];

  filters.forEach((filter) => {
    groupedCv[filter as keyof typeof groupedCv] = cv.filter(
      (item) => item?.type === filter,
    );
  });

  return groupedCv;
}

type GroupProps = {
  block: ICvFields;
};

const GroupBlock = ({ block }: GroupProps): JSX.Element => {
  const { type, startYear, endYear, description, descriptionRich } = block;
  const isAward = type === 'award';

  return (
    <div
      className={clsx(!isAward ? 'timeline pl-12' : 'pl-6', 'pb-4 last:pb-0')}
    >
      {!isAward && (startYear || endYear) && (
        <p className="timeline-dot text-primary font-bold">
          {startYear} {startYear && endYear ? '-' : ''} {endYear}
        </p>
      )}
      <div
        className={clsx(
          !isAward && !startYear && !endYear && 'timeline-dot',
          'text-primary flex',
        )}
      >
        {isAward && (
          <Icon
            className="w-5 mr-2 shrink-0"
            type="RoundCheck"
            color={tailwind.theme.backgroundColor.secondary}
          />
        )}
        {descriptionRich ? (
          <div className="text-primary">
            <RichText fields={descriptionRich} />
          </div>
        ) : (
          <p
            className={clsx(
              !isAward && !startYear && !endYear && 'timeline-dot',
              'text-primary flex',
            )}
          >
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

type CVProps = {
  items?: ICvFields[];
};

const CV = ({ items }: CVProps): JSX.Element | null => {
  if (!items?.length) {
    return null;
  }

  const groupedCv = groupCv(items);
  const { t } = useTranslation();

  return (
    <Accordion>
      {groupedCv.education.length > 0 && (
        <Accordion.Item
          title={`${t('doctorProfile.cv.educationTitle')} (${
            groupedCv.education.length
          })`}
        >
          {groupedCv.education.map((block) => (
            <GroupBlock key={block.id} block={block} />
          ))}
        </Accordion.Item>
      )}

      {groupedCv.professional.length > 0 && (
        <Accordion.Item
          title={`${t('doctorProfile.cv.careerTitle')} (${
            groupedCv.professional.length
          })`}
        >
          {groupedCv.professional.map((block) => (
            <GroupBlock key={block.id} block={block} />
          ))}
        </Accordion.Item>
      )}

      {groupedCv.award.length > 0 && (
        <Accordion.Item
          title={`${t('doctorProfile.cv.awardsTitle')} (${
            groupedCv.award.length
          })`}
        >
          {groupedCv.award.map((block) => (
            <GroupBlock key={block.id} block={block} />
          ))}
        </Accordion.Item>
      )}
    </Accordion>
  );
};

export default CV;
